import React, { useMemo, useState } from "react"
import { Tabs, Tooltip, Switch } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import {
  faCopy,
  faFileExcel,
  faFileArrowDown,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import BarChart from "shared/ui/Charts/BarChart.component"
import {
  convertDataToTable,
  BarChartDataItem,
  TransformedTableData,
} from "utils/convertDataToTable"
import { buildWorkbook, downloadWorkbook } from "utils/excel/exportTableModels"
import { downloadJSON } from "utils/files/downloadUtils"
import { jsonPretty } from "utils/jsonPretty"
import DataTable from "shared/ui/Table/DataTable/DataTable.component"
import { resolveTableStyle } from "shared/ui/Table/style/looks"
import { useTableStyleStore } from "stores/tableStyleStore"
import ConsoleTableStyleButton from "./ConsoleTableStyleButton.component"
import { buildTableModel, TableModel } from "utils/tableModel"
import {
  EngineOutputItem,
  RTableContent,
  WeightingReport,
} from "types/engineOutput.types"
import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import WeightingReportView from "./WeightingReport/WeightingReport.component"

// Define the props interface for the OutputTabs component
interface OutputTabsProps {
  /** The result object containing the data and other information */
  result: {
    output?: EngineOutputItem[]
    log?: string[]
    weighting?: WeightingReport[]
  }
}

// Define the structure for chart data expected by BarChart
interface ChartData {
  data: BarChartDataItem[]
  keys: string[]
  statisticTypes: STATISTIC_TYPE[]
  xAxisLabel: string
}

// Define the structure for tab items
interface TabItem {
  key: string
  label: string
  children: React.ReactNode
}

/** Type guard to check if content is RTableContent. */
const isRTableContent = (content: unknown): content is RTableContent =>
  typeof content === "object" &&
  content !== null &&
  "data" in content &&
  Array.isArray((content as RTableContent).data)

/** Whether an output item is an engine table (shown with DataTable unless it is text). */
const isRtableItem = (item: EngineOutputItem): boolean =>
  item.format !== "json" && isRTableContent(item.content)

/**
 * OutputTabs component to render tabbed output from a result object.
 * It supports rendering data as tables, charts, or JSON, and includes options to copy, export, or download the data.
 *
 * @param props - The component props
 * @returns {JSX.Element|null} A component that renders the result in tabbed format, or null if no result is available.
 */
const OutputTabs: React.FC<OutputTabsProps> = ({ result }) => {
  const [chartActive, setChartActive] = useState<boolean>(false)
  const consoleChoice = useTableStyleStore((s) => s.consoleChoice)
  const tableStyle = useMemo(
    () => resolveTableStyle("console", consoleChoice),
    [consoleChoice]
  )
  // One model per table item, built once per output: a style change then
  // re-renders the tables without rebuilding them (null: not a table).
  const output = result?.output
  const tableModels = useMemo(
    () =>
      (output ?? []).map((item) =>
        item.format !== "text" && isRtableItem(item)
          ? buildTableModel(item.content as RTableContent)
          : null
      ),
    [output]
  )

  if (!result || !result.log) {
    return null
  }

  const weighting = result.weighting ?? []
  if ((result.output?.length ?? 0) === 0 && weighting.length === 0) {
    return (
      <p className="p-2">
        No Output in result. Please check if query has output. See log below
      </p>
    )
  }

  /**
   * Copies the given content to the clipboard and displays a success message.
   *
   * @param content - The content to copy to the clipboard.
   */
  const CopyToClipboard = (content: unknown): void => {
    const contentToCopy = jsonPretty(content)
    navigator.clipboard.writeText(contentToCopy)
    notify.toast.success("Output copied to clipboard")
  }

  const tabItems: TabItem[] = (result.output ?? []).map((item, index) => {
    const isRtable = isRtableItem(item)
    let content: string | TableModel

    if (item.format === "text") {
      content =
        typeof item.content === "string" ? item.content : String(item.content)
    } else {
      // A table's model (built above), else the content as JSON
      content = tableModels[index] ?? jsonPretty(item.content)
    }

    const contentTooLong =
      !isRtable && typeof content === "string" && content.length > 1000000

    // The chart stays on the legacy conversion, built only while it is shown.
    const chartTable: TransformedTableData | null =
      isRtable && chartActive
        ? convertDataToTable(item.content as RTableContent)
        : null

    // Extract chart data for bar chart rendering
    const chartData: ChartData | null =
      chartTable && chartTable.barChartData
        ? {
            data: chartTable.barChartData.data as BarChartDataItem[],
            keys: chartTable.barChartData.keys,
            statisticTypes: chartTable.barChartData
              .statisticTypes as STATISTIC_TYPE[],
            xAxisLabel: chartTable.barChartData.xAxisLabel,
          }
        : null

    return {
      key: `${index + 1}`,
      label: item.label || `Table ${index + 1}`,
      children: (
        <div className="flex flex-col w-full h-full">
          <div className="flex justify-between items-center p-3 shadow-sm">
            <div className="flex items-center space-x-4">
              {isRtable && (
                <>
                  <Switch
                    style={{ backgroundColor: "var(--dq-brand)" }}
                    checkedChildren="Chart"
                    unCheckedChildren="Table"
                    checked={chartActive}
                    onChange={(checked: boolean) => setChartActive(checked)}
                  />
                </>
              )}
            </div>
            <div className="flex space-x-3">
              {result.output && result.output.length >= 1 && (
                <Tooltip title="Download All Outputs">
                  <FontAwesomeIcon
                    icon={faFileArrowDown}
                    style={{ color: "var(--dq-brand)" }}
                    className="cursor-pointer"
                    onClick={() => downloadJSON(result.output!, `Output`)}
                  />
                </Tooltip>
              )}
              <Tooltip title="Copy to clipboard">
                <FontAwesomeIcon
                  icon={faCopy}
                  style={{ color: "var(--dq-brand)" }}
                  className="cursor-pointer"
                  onClick={() => CopyToClipboard(item.content)}
                />
              </Tooltip>
              {isRtable && (
                <>
                  <ConsoleTableStyleButton />
                  <Tooltip title="Export to Excel">
                    <FontAwesomeIcon
                      icon={faFileExcel}
                      style={{ color: "var(--dq-brand)" }}
                      className="cursor-pointer"
                      onClick={async () => {
                        const name = item.label || `Table_${index + 1}`
                        try {
                          const model =
                            typeof content === "string"
                              ? buildTableModel(item.content as RTableContent)
                              : content
                          const workbook = buildWorkbook(
                            [{ name, model }],
                            { mode: "separate", style: tableStyle }
                          )
                          await downloadWorkbook(workbook, name)
                        } catch (error) {
                          notify.error(
                            "Excel export failed",
                            error instanceof Error
                              ? error.message
                              : "Unknown error"
                          )
                        }
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </div>
          </div>
          <div className="h-full overflow-auto px-2">
            {item.format === "text" ? (
              <pre className="m-0 p-0">{content as string}</pre>
            ) : isRtable ? (
              chartActive && chartTable && chartData ? (
                <BarChart
                  isChart={!chartTable.hasSubHeaders}
                  data={chartData.data}
                  keys={chartData.keys}
                  statisticTypes={chartData.statisticTypes}
                  xAxisLabel={chartData.xAxisLabel}
                />
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <DataTable
                    model={content as TableModel}
                    style={tableStyle}
                    layout="fill"
                  />
                </div>
              )
            ) : contentTooLong ? (
              <p className="p-2">
                Content is too large to display. Click{" "}
                <a
                  className="text-blue-500 cursor-pointer"
                  onClick={() =>
                    downloadJSON(
                      item.content,
                      item.label || `Output_${index + 1}`
                    )
                  }
                >
                  here
                </a>{" "}
                to download the full content.
              </p>
            ) : (
              <pre className="m-0 p-0">{content as string}</pre>
            )}
          </div>
        </div>
      ),
    }
  })

  if (weighting.length > 0) {
    tabItems.push({
      key: "weighting",
      label: "Weighting",
      children: (
        <div className="h-full overflow-auto">
          {weighting.map((r, i) => (
            <WeightingReportView key={`${r.weights}-${i}`} report={r} />
          ))}
        </div>
      ),
    })
  }

  return (
    <div className="h-full w-full overflow-auto">
      <Tabs
        defaultActiveKey="1"
        items={tabItems}
        className="data-reporting-tabs"
      />
    </div>
  )
}

export default OutputTabs
